/**
 * @fileoverview Module for `src/admin/Groups.js`.
 */

import {
  Button,
  List,
  Datagrid,
  TextField,
  DateField,
  Filter,
  TextInput,
  BulkExportButton,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  Create,
  SimpleForm,
  required,
  regex,
  minLength,
  Toolbar,
  SaveButton,
  Edit,
  EditButton,
  useNotify,
} from 'react-admin';
import { Link } from 'react-router-dom';
import propTypes from 'prop-types';
import queryString from 'query-string';

import GroupImport from './GroupImport';
import { currentElectionDate } from '../lib/dates';
import { useAuth } from '../contexts/AuthContext';

/**
 * Edit toolbar for group records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Toolbar with save action.
 */
const GroupEditToolbar = (props) => (
//removes default delete option from toolbar
  <Toolbar { ...props } >
    <SaveButton />
  </Toolbar>
);

/**
 * Maps group API failures to notification messages.
 *
 * @param {Object} error
 * @param {Function} notify
 * @returns {void}
 */
const errorHandler = (error, notify) => {
  if (error.status === 'Conflict') {
    notify(`Error: Name and Slug must be Unique`);
  } else {
    notify(`Error: ${ error.body || error.message }`);
  }
};

/**
 * Optional group inputs used by create and edit forms.
 *
 * @returns {JSX.Element} Optional group controls.
 */
const OptionalInputs = () => (
  <ReferenceArrayInput
    allowEmpty
    filter={{ 'electionDate:greaterThanOrEqualTo': currentElectionDate() }}
    helperText='Districts this group should see.'
    label='districts'
    perPage={ 100 }
    reference='Districts'
    source='districtIds'
  >
    <AutocompleteArrayInput optionText='districtId' />
  </ReferenceArrayInput>
);

/**
 * Edit view for group records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Group editor.
 */
function GroupEdit(props) {
  const notify = useNotify();
  const auth = useAuth();
  /**
   * Delegates group edit errors to the shared error handler for admin notification.
   */
  const onFailure = (error) => {
    errorHandler(error, notify);
  };
  return (
    <div>
      <Edit onFailure={ onFailure } undoable={ false } { ...props }>
        <SimpleForm redirect='edit' toolbar={ <GroupEditToolbar /> }>
          <TextInput disabled source='id' />
          <TextInput disabled source='slug' />
          <TextInput
            source='name'
            validate={ [required(), minLength(3)] }
          />
          <OptionalInputs />
          <Link
            className='button'
            to={{
              pathname: `/EmailPreferences`,
              search: queryString.stringify({
                displayedFilters: JSON.stringify({ 'groups.id': true }),
                filter: JSON.stringify({ groups: { id: parseInt(props.id) }}),
                order: 'ASC',
                perPage: 10,
                page: 1,
                sort: 'id',
              }),
            }}
          >
            <Button color='primary' label='Show Group Users' />
          </Link><br />
          <GroupImport auth={ auth } title='Import Group Users' { ...props } groupId={ props.id } />
        </SimpleForm>
      </Edit>
    </div>
  );
}

GroupEdit.propTypes = {
  id: propTypes.number,
};

/**
 * Toolbar used by group create forms.
 *
 * @param {Object} props
 * @returns {JSX.Element} Create toolbar.
 */
const GroupCreateToolbar = (props) => (
  <Toolbar { ...props } >
    <SaveButton
      label='create'
    />
  </Toolbar>
);

/**
 * Create view for group records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Group creation form.
 */
export const GroupCreate = (props) => {
  const notify = useNotify();
  /**
   * Delegates group creation errors to the shared error handler for admin notification.
   */
  const onFailure = (error) => {
    errorHandler(error, notify);
  };
  return (
    <Create onFailure={ onFailure } { ...props }>
      <SimpleForm toolbar={ <GroupCreateToolbar /> }>
        <TextInput
          label='Group name'
          source='name'
          validate={ [required(), minLength(3)] }
        />
        <TextInput
          helperText='This cannot be changed or deleted.'
          label='Group slug'
          source='slug'
          validate={ [
            required(),
            regex(/^[a-z\d]*$/, 'Slug must be alphanumeric with lowercase letters'),
            regex(/[a-z]/, "Slug can't be all numbers"),
            minLength(3),
          ] }
        />
        <OptionalInputs />
      </SimpleForm>
    </Create>
  );
};

/**
 * Bulk action controls for group lists.
 *
 * @param {Object} props
 * @returns {JSX.Element} Bulk action buttons.
 */
const GroupListBulkActionButtons = (props) => (
  <BulkExportButton { ...props } />
);

/**
 * Filter controls for group list queries.
 *
 * @param {Object} props
 * @returns {JSX.Element} Group filters.
 */
const GroupFilter = (props) => (
  <Filter { ...props }>
    <TextInput alwaysOn label='Name' source='name:contains' />
    <TextInput alwaysOn label='Slug' source='slug:contains' />
  </Filter>
);

/**
 * List view for group records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Group data grid.
 */
const GroupList = (props) => (
  <div>
    <List { ...props } bulkActionButtons={ <GroupListBulkActionButtons /> } filters={ <GroupFilter /> } >
      <Datagrid>
        <TextField source='id' />
        <TextField source='slug' />
        <TextField source='name' />
        <DateField showTime source='createdAt' />
        <EditButton { ...props } />
      </Datagrid>
    </List>
  </div>
);

export default GroupList;
export { GroupEdit };
